// @ts-nocheck
/* eslint-disable react/prop-types */
// SolyticsInput.jsx
import { useState, useMemo, useRef, useDeferredValue } from 'react';
import { TextField, FormHelperText, InputAdornment } from '@mui/material';
import SolyticsBox from './SolyticsBox';
import SolyticsTooltip from './SolyticsTooltip';
import SolyticsButton from './SolyticsButton';
import SolyticsLabel from './SolyticsLabel';
import NumberField from './Internals/NumberField';
import { alpha } from '@mui/material/styles';

const INPUT_FIELD_SIZE = '280px';

/* ------------------ DEFAULT CONSTANTS ------------------ */
const DEFAULT_VARIANT = 'standard';
const DEFAULT_SIZE = 'medium';
const DEFAULT_TYPE = 'text';

// Key Style controllers
const SMALL_INPUT_PADDING = '6px 12px';
const MEDIUM_INPUT_PADDING = '8px 12px';
const INPUT_FIELD_BORDER_RADIUS = 0.5; // 0.5px border appearance

/* ------------------ THEME TOKENS ------------------ */
const getThemeTokens = (theme) => ({
  focusBorderColor:
    theme.palette.lavenderBlue?.[80] || theme.palette.primary?.main,
  defaultBorderColor: theme.palette.divider || theme.palette.grey?.[60],
  backgroundColor: theme.palette.background?.default,
  focusedBackgroundColor: theme.palette.background?.paper || '#FFFFFF',
  textColor: theme.palette.text?.secondary,
  placeholderColor: theme.palette.text?.secondary,
  disabledPlaceholderColor: theme.palette.text?.disabled,
  labelColor: theme.palette.text?.secondary,
  helperTextColor: theme.palette.text?.secondary,
  errorColor: theme.palette.error?.main,
  characterCountColor: theme.palette.grey?.T30,
  focusBoxShadowColor: alpha(
    theme.palette.lavenderBlue?.[20] || theme.palette.primary?.light,
    0.5
  ),
});

/* ------------------ STYLE GENERATORS ------------------ */
const SIZE_PADDING = {
  small: SMALL_INPUT_PADDING,
  medium: MEDIUM_INPUT_PADDING,
};

const getTextFieldStyles = (theme, { error, disabled, readOnly, size }) => {
  const tokens = getThemeTokens(theme);

  return {
    '& .MuiInputLabel-root': {
      fontSize: 18,
      fontWeight: 500,
      color: error ? tokens.errorColor : tokens.labelColor,
      '&.Mui-focused': {
        color: error ? tokens.errorColor : tokens.focusBorderColor,
      },
      '&.Mui-disabled': {
        color: tokens.disabledPlaceholderColor,
      },
    },
    '& .MuiInputBase-root': {
      borderRadius: INPUT_FIELD_BORDER_RADIUS,
      backgroundColor: tokens.backgroundColor,
      border: '1px solid',
      borderColor: error ? tokens.errorColor : tokens.defaultBorderColor,
      transition: theme.transitions.create([
        'border-color',
        'background-color',
        'box-shadow',
      ]),
      '&:hover': {
        borderColor: (() => {
          if (disabled) return tokens.defaultBorderColor;
          if (error) return tokens.errorColor;
          return tokens.focusBorderColor;
        })(),
      },
      '&.Mui-focused': {
        backgroundColor: tokens.focusedBackgroundColor,
        boxShadow: error
          ? `${alpha(tokens.errorColor, 0.1)} 0 0 0 0.2rem`
          : `${tokens.focusBoxShadowColor} 0 0 0 0.2rem`,
        borderColor: error ? tokens.errorColor : tokens.focusBorderColor,
      },
      '&.Mui-disabled': {
        opacity: 0.6,
      },
      '& .MuiInputAdornment-root': {
        color: tokens.placeholderColor,
        '&.MuiInputAdornment-positionStart': {
          marginRight: 0,
        },
        '&.MuiInputAdornment-positionEnd': {
          marginLeft: 0,
        },
      },
    },
    '& .MuiInputBase-input': {
      fontSize: 14,
      padding: SIZE_PADDING[size] || SIZE_PADDING.medium,
      fontFamily: theme.typography.fontFamily,
      color: tokens.textColor,
      border: 'none',
      backgroundColor: 'transparent',
      cursor: (() => {
        if (disabled) return 'not-allowed';
        if (readOnly) return 'default';
        return 'text';
      })(),
      '&::placeholder': {
        color: disabled
          ? tokens.disabledPlaceholderColor
          : tokens.placeholderColor,
        opacity: 1,
      },
      '&:focus': {
        outline: 0,
      },
      '&:disabled': {
        cursor: 'not-allowed',
      },
    },
    '&.MuiInputBase-adornedStart > .MuiInputBase-input': {
      paddingLeft: '0px',
    },
    '&.MuiInputBase-adornedEnd > .MuiInputBase-input': {
      paddingRight: '12px',
    },
    '&.MuiInputBase-multiline > .MuiInputBase-input': {
      padding: SIZE_PADDING[size] || SIZE_PADDING.medium,
    },
    '& .MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: 'none',
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginTop: 0.5,
      fontSize: 12,
      color: error ? tokens.errorColor : tokens.helperTextColor,
    },
  };
};

/**
 * SolyticsInput - A unified input component supporting text, number, file, and select types
 *
 * @component
 * @description
 * SolyticsInput is a comprehensive form input wrapper built on MUI TextField, FormControl, and custom NumberField.
 * It consolidates text, number, file upload, and select inputs into a single component with shared styling,
 * label rendering via SolyticsLabel, helper text with error state, and character count support.
 *
 * @param {string} id - HTML id attribute for the input
 * @param {string} name - Name attribute for the input (used to generate id if not provided)
 * @param {string} label - Label text rendered above the input via SolyticsLabel
 * @param {*} value - Current value of the input
 * @param {function} onChange - Change handler; receives the event object
 * @param {boolean} [error=false] - If true, renders error styling on border, label, and helper text
 * @param {string|React.ReactNode} helperText - Helper text displayed below the input
 * @param {boolean} [disabled=false] - If true, disables the input
 * @param {boolean} [readOnly=false] - If true, makes the input read-only
 * @param {boolean} [required=false] - If true, marks the field as required
 * @param {string} [variant='standard'] - MUI TextField variant: 'standard' | 'outlined' | 'filled' | 'select' (select is used internally by SolyticsDropdownInput/SolyticsAutocomplete)
 * @param {string} [size='medium'] - Input size: 'small' | 'medium'
 * @param {boolean} [fullWidth=false] - If true, input takes full container width
 * @param {string} placeholder - Placeholder text
 * @param {boolean} [multiline=false] - If true, renders a textarea
 * @param {number} rows - Fixed number of rows for multiline
 * @param {number} minRows - Minimum rows for multiline auto-resize
 * @param {number} maxRows - Maximum rows for multiline auto-resize
 * @param {string} [type='text'] - Input type: 'text' | 'number' | 'file' | 'password' | 'email' | etc.
 * @param {React.ReactNode} startAdornment - Element rendered at the start of the input
 * @param {React.ReactNode} endAdornment - Element rendered at the end of the input
 * @param {Object} infoIcon - Info icon config: { tooltip: string, placement?: string, icon?: ReactNode }
 * @param {boolean} [showCharCount=false] - If true, displays character count
 * @param {number} maxCharacterAllowed - Maximum characters allowed (enforced + displayed)
 * @param {number} min - Minimum value (for number type)
 * @param {number} max - Maximum value (for number type)
 * @param {number} step - Step increment (for number type)
 * @param {*} defaultValue - Default value for uncontrolled usage
 * @param {boolean} [multiple=false] - If true, allows multiple file selection (file type)
 * @param {string} accept - Accepted file types (file type)
 * @param {boolean} [autoFocus=false] - If true, auto-focuses the input on mount
 * @param {Object} slotProps - MUI slotProps passed to TextField (input, inputLabel, htmlInput)
 * @param {function} onFocus - Focus event handler
 * @param {function} onBlur - Blur event handler
 * @param {function} onKeyDown - Keydown event handler  // <-- ADDED
 * @param {boolean} select - If true, renders as a select field
 * @param {string} styleVariant - Visual style preset: 'time-hour' | 'time-minute' | 'time-period' | 'calendar-month' | 'calendar-year' | 'calendar-input' | 'color-format-main' | 'color-format-popover' | 'dropdown-shadow-none'
 *
 * @returns {React.ReactElement} A form input element
 */
const SolyticsInput = (props) => {
  const {
    id,
    name,
    label,
    value,
    onChange,
    error = false,
    helperText,
    disabled = false,
    readOnly = false,
    required = false,
    variant = DEFAULT_VARIANT,
    size = DEFAULT_SIZE,
    fullWidth = false,
    placeholder,
    multiline = false,
    rows,
    minRows,
    maxRows,
    type = DEFAULT_TYPE,
    startAdornment,
    endAdornment,
    infoIcon,
    showCharCount = false,
    maxCharacterAllowed,
    autoFocus = false,
    slotProps,
    onFocus,
    onBlur,
    onKeyDown,
    select,
    // FileInput props
    multiple = false,
    accept,
    // NumberField props
    min,
    max,
    step,
    defaultValue,
    allowDecimal = true,
    disableScrollChange = true,
    // For internal use only (private)
    styleVariant,
  } = props;

  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  /* ------------------ HELPERS ------------------ */
  const currentLength = useDeferredValue(value ? String(value).length : 0);
  const showCount = showCharCount && maxCharacterAllowed;
  const inputId = id || `${name}-input`;

  /* ------------------ STYLE VARIANT ------------------ */
  const { variantSx, variantInputProps, BOX_SIZE } = useMemo(() => {
    let _variantSx = {};
    let _variantInputProps = {};
    let _BOX_SIZE = fullWidth ? '100%' : INPUT_FIELD_SIZE;

    switch (styleVariant) {
      case 'time-hour':
        _variantSx = {
          width: '60px',
          '& .MuiInputBase-root': {
            borderRadius: '8px 0px 0px 8px',
          },
        };
        _variantInputProps = { maxLength: 2, 'aria-label': 'Hour' };
        _BOX_SIZE = '60px';
        break;

      case 'time-minute':
        _variantSx = {
          width: '60px',
          '& .MuiInputBase-root': {
            borderRadius: '0px',
          },
        };
        _variantInputProps = { maxLength: 2, 'aria-label': 'Minute' };
        _BOX_SIZE = '60px';
        break;

      case 'time-period':
        _variantSx = {
          width: '80px',
          '& .MuiInputBase-root': {
            fontSize: '14px',
            borderRadius: '0px 8px 8px 0px',
          },
        };
        _variantInputProps = {};
        _BOX_SIZE = '80px';
        break;

      case 'calendar-month':
        _variantSx = {
          width: '150px',
          cursor: 'pointer',
          '& .MuiInputBase-root': {
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            '&.Mui-focused': {
              boxShadow: 'none',
              border: 'none',
            },
            '&:hover': {
              borderColor: 'transparent',
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '16px',
            fontWeight: 600,
            padding: '4px 0',
            textAlign: 'right',
          },
        };
        _variantInputProps = {};
        _BOX_SIZE = '150px';
        break;

      case 'calendar-year':
        _variantSx = {
          width: '120px',
          '& .MuiInputBase-root': {
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            '&.Mui-focused': {
              boxShadow: 'none',
              border: 'none',
            },
            '&:hover': {
              borderColor: 'transparent',
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '16px',
            fontWeight: 600,
            padding: '4px 0',
            textAlign: 'right',
          },
        };
        _variantInputProps = {};
        _BOX_SIZE = '120px';
        break;

      case 'calendar-input':
        _variantSx = {};
        _variantInputProps = { 'aria-haspopup': 'dialog' };
        break;

      case 'color-format-main':
        _variantSx = {
          width: 120,
          '& .MuiInputBase-root': {
            border: 'none',
            boxShadow: 'none',
            padding: 0,
            pr: 1,
            margin: 0,
            '&.Mui-focused': {
              boxShadow: 'none',
              border: 'none',
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '15px',
            fontWeight: 500,
            pl: 1.5,
          },
        };
        _variantInputProps = {};
        _BOX_SIZE = '90px';
        break;

      case 'color-format-popover':
        _variantSx = {
          width: 80,
          '& .MuiInputBase-root': {
            fontSize: '13px',
            fontWeight: 500,
            minHeight: '36px',
            background: (theme) => theme.palette.background.default,
            color: (theme) => theme.palette.text.primary,
            padding: 0,
            px: 1,
            margin: 0,
            borderRadius: '4px',
            borderColor: (theme) => theme.palette.grey[60],
          },
        };
        _variantInputProps = {};
        _BOX_SIZE = '90px';
        break;

      case 'dropdown-shadow-none':
        _BOX_SIZE = '100%';
        _variantSx = {
          '& .MuiInputBase-root': {
            '&.Mui-focused': {
              boxShadow: 'none',
              backgroundColor: 'transparent',
            },
            '&.Mui-focused fieldset': { border: 'none' },
          },
        };
        break;
      case 'search-flat':
        _BOX_SIZE = '100%';
        _variantSx = {
          width: '100%',
          '& .MuiOutlinedInput-root': {
            // Override the hardcoded borderRadius:'8px' from baseTextFieldSx
            borderRadius: 0,
            backgroundColor: 'transparent',
            // Remove all border states
            '& fieldset': { border: 'none', borderRadius: 0 },
            '&:hover fieldset': { border: 'none' },
            // Override the default focus ring + background from baseTextFieldSx
            '&.Mui-focused': {
              boxShadow: 'none',
              backgroundColor: 'transparent',
            },
            '&.Mui-focused fieldset': { border: 'none' },
          },
        };
        _variantInputProps = {};
        break;

      case 'table-settings-search':
        _BOX_SIZE = '100%';
        break;

      default:
        _variantSx = {};
        _variantInputProps = {};
        break;
    }

    return {
      variantSx: _variantSx,
      variantInputProps: _variantInputProps,
      BOX_SIZE: _BOX_SIZE,
    };
  }, [styleVariant, fullWidth]);

  /* ------------------ EVENT HANDLERS ------------------ */
  const handleFocus = (event) => {
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event) => {
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleKeyDown = (event) => {
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  /* ------------------ FILE INPUT HANDLERS ------------------ */
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    onChange(event);
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  /* ------------------ ADORNMENTS ------------------ */
  const renderStartAdornment =
    slotProps?.input?.startAdornment ||
    (startAdornment ? (
      <InputAdornment sx={{ p: 1 }} position="start">
        {startAdornment}
      </InputAdornment>
    ) : undefined);

  const renderEndAdornment =
    slotProps?.input?.endAdornment ||
    (endAdornment ? (
      <InputAdornment position="end">{endAdornment}</InputAdornment>
    ) : undefined);

  const resolvedInputProps = {
    ...slotProps?.input,
    readOnly,
    startAdornment: renderStartAdornment,
    endAdornment: renderEndAdornment,
  };

  const resolvedInputLabelProps = {
    shrink: true,
    ...slotProps?.inputLabel,
  };

  const resolvedHtmlInputProps = {
    ...slotProps?.htmlInput,
    ...variantInputProps,
    'aria-invalid': error,
    'aria-required': required,
  };

  const inputVariant = useMemo(() => {
    if (variant === 'outlined') return 'outlined';
    if (variant === 'filled') return 'filled';
    return 'standard';
  }, [variant]);

  const helperTextContent = useMemo(() => {
    if (!helperText && !showCount) return null;

    return (
      <FormHelperText
        error={error}
        sx={{
          marginLeft: 0,
          marginTop: 0.5,
          fontSize: 12,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
          gap: 1,
        }}
      >
        <SolyticsBox
          component="span"
          sx={{
            flex: '1 1 auto',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {helperText}
        </SolyticsBox>
        {showCount && (
          <SolyticsBox
            component="span"
            sx={{
              fontSize: '12px',
              color: (theme) =>
                currentLength >= maxCharacterAllowed
                  ? theme.palette.error?.main
                  : theme.palette.grey?.T30,
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {currentLength}/{maxCharacterAllowed}
          </SolyticsBox>
        )}
      </FormHelperText>
    );
  }, [helperText, showCount, currentLength, maxCharacterAllowed, error]);

  const getFileNames = () => {
    if (selectedFiles.length === 0) return '';
    if (selectedFiles.length === 1) return selectedFiles[0].name;
    return `${selectedFiles.length} files selected`;
  };

  const getAllFileNamesTooltip = () => {
    if (selectedFiles.length < 1) return '';
    return selectedFiles.map((file) => file.name).join('\n');
  };

  return (
    <SolyticsBox
      {...slotProps?.root}
      sx={[
        {
          width: BOX_SIZE,
          display: 'inline-flex',
          flexDirection: 'column',
          position: 'relative',
          minWidth: 0,
          verticalAlign: 'top',
        },
        slotProps?.root?.sx,
      ]}
    >
      {label && (
        <SolyticsLabel
          id={inputId}
          label={label}
          required={required}
          disabled={disabled}
          infoIcon={infoIcon}
        />
      )}
      {type === 'file' && (
        <>
          {' '}
          <input
            ref={fileInputRef}
            type="file"
            id={id}
            name={name}
            onChange={handleFileChange}
            multiple={multiple}
            accept={accept}
            disabled={disabled}
            required={required}
            aria-labelledby={label ? inputId : undefined}
            // aria-describedby={helperId}
            aria-invalid={error}
            style={{ display: 'none' }}
          />
          <SolyticsBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              border: '1px solid',
              borderColor: error
                ? (theme) => theme.palette.error.main
                : (theme) => theme.palette.divider,
              borderRadius: '8px',
              padding: '6px 6px',
              backgroundColor: disabled
                ? (theme) => theme.palette.action.disabledBackground
                : (theme) => theme.palette.background?.paper,
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:hover': {
                borderColor: disabled
                  ? (theme) => theme.palette.divider
                  : error
                    ? (theme) => theme.palette.error.main
                    : (theme) => theme.palette.lavenderBlue?.[80],
              },
              '&:focus-within': {
                borderColor: error
                  ? (theme) => theme.palette.error.main
                  : (theme) => theme.palette.lavenderBlue?.[80],
                boxShadow: (theme) =>
                  `0 0 0 4px ${error ? theme.palette.error.light : theme.palette.lavenderBlue?.[20]}`,
              },
            }}
          >
            <SolyticsButton
              variant="outlined"
              size={size}
              onClick={handleFileButtonClick}
              disabled={disabled}
            >
              Choose {multiple ? 'Files' : 'File'}
            </SolyticsButton>
            <SolyticsTooltip
              text={getAllFileNamesTooltip()}
              placement="top"
              variant="default"
            >
              <SolyticsBox
                sx={{
                  flex: 1,
                  color:
                    selectedFiles.length > 0
                      ? (theme) => theme.palette.text?.primary
                      : (theme) => theme.palette.text?.secondary,
                  fontSize: '14px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: selectedFiles.length > 1 ? 'help' : 'default',
                }}
              >
                {getFileNames() || placeholder || 'No file chosen'}
              </SolyticsBox>
            </SolyticsTooltip>
          </SolyticsBox>
        </>
      )}
      {type === 'number' && (
        <NumberField
          id={inputId}
          name={name}
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          size={size}
          fullWidth={fullWidth}
          placeholder={placeholder}
          startAdornment={startAdornment}
          endAdornment={endAdornment}
          min={min}
          max={max}
          step={step}
          defaultValue={defaultValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown} // <-- ADDED
          showCharCount={showCharCount}
          maxCharacterAllowed={maxCharacterAllowed}
          autoFocus={autoFocus}
          allowDecimal={allowDecimal}
          disableScrollChange={disableScrollChange}
        />
      )}
      {type !== 'file' && type !== 'number' && (
        <TextField
          id={inputId}
          name={name}
          value={value || ''}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown} // <-- ADDED
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          required={required}
          fullWidth={fullWidth}
          variant={inputVariant}
          size={size}
          multiline={multiline}
          rows={rows}
          minRows={minRows}
          maxRows={maxRows}
          autoFocus={autoFocus}
          type={type}
          select={select}
          className="solytics-input-field"
          sx={[
            (theme) =>
              getTextFieldStyles(theme, {
                label,
                error,
                variant,
                disabled,
                readOnly,
                size,
              }),
            variantSx,
          ]}
          slotProps={{
            input: resolvedInputProps,
            htmlInput: resolvedHtmlInputProps,
            inputLabel: resolvedInputLabelProps,
          }}
        />
      )}
      {helperTextContent}
    </SolyticsBox>
  );
};

export default SolyticsInput;

/**
 * ## Features
 * - Unified input supporting text, textarea, number (via NumberField), and file upload types
 * - External label rendered via SolyticsLabel with info icon tooltip support
 * - Error state propagation to border, label, and helper text (via FormHelperText)
 * - Character count display with limit enforcement
 * - Start/end adornment support with merging logic for Autocomplete/Dropdown integration
 * - StyleVariant system for visual presets (time-hour, calendar-month, color-format, etc.)
 * - Full theme integration using getThemeTokens (border, focus, hover, disabled states)
 * - WCAG 2.1 AA compliant with proper aria attributes
 *
 * ## Supported `type` values
 * - `text` (default): Standard MUI TextField
 * - `number`: Custom NumberField with increment/decrement buttons
 * - `file`: Custom file upload with choose button and file name display
 *
 * ## StyleVariant presets
 * - `time-hour`, `time-minute`, `time-period`: For time selector inputs
 * - `calendar-month`, `calendar-year`: Borderless bold dropdowns for calendar navigation
 * - `calendar-input`: Standard input with aria-haspopup for date picker trigger
 * - `color-format-main`, `color-format-popover`: Compact inputs for color picker
 * - `dropdown-shadow-none`: Full-width dropdown without focus shadow
 *
 * ## Examples
 *
 * ```jsx
 * // Basic text input
 * <SolyticsInput name="email" label="Email" value={email} onChange={handleChange} />
 *
 * // Number input with min/max
 * <SolyticsInput name="age" label="Age" type="number" min={0} max={120} value={age} onChange={handleChange} />
 *
 * // File upload
 * <SolyticsInput name="doc" label="Document" type="file" accept=".pdf" onChange={handleChange} />
 *
 * // With error and helper text
 * <SolyticsInput name="field" label="Field" error helperText="This field is required" />
 *
 * // With info icon tooltip
 * <SolyticsInput name="field" label="Field" infoIcon={{ tooltip: 'Help text' }} />
 * ```
 */
